import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [empresa, setEmpresa] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isImpersonating, setIsImpersonating] = useState(false);

  useEffect(() => {
    // Check for impersonation mode - localStorage flag
    const impersonateActive = localStorage.getItem("impersonateActive") === "true";
    
    if (impersonateActive) {
      const impersonateToken = localStorage.getItem("impersonateToken");
      const impersonateUser = localStorage.getItem("impersonateUser");
      const impersonateEmpresa = localStorage.getItem("impersonateEmpresa");
      
      if (impersonateToken && impersonateUser && impersonateEmpresa) {
        try {
          const parsedUser = JSON.parse(impersonateUser);
          const parsedEmpresa = JSON.parse(impersonateEmpresa);
          
          setToken(impersonateToken);
          setUser(parsedUser);
          setEmpresa(parsedEmpresa);
          setIsImpersonating(true);
          axios.defaults.headers.common["Authorization"] = `Bearer ${impersonateToken}`;
          setLoading(false);
          return;
        } catch (e) {
          console.error("Error parsing impersonation data:", e);
          // Clear invalid data
          localStorage.removeItem("impersonateActive");
        }
      }
    }
    
    // Normal authentication flow
    const savedToken = localStorage.getItem("token");
    if (savedToken) {
      setToken(savedToken);
      axios.defaults.headers.common["Authorization"] = `Bearer ${savedToken}`;
      // Load empresa info from localStorage
      const savedEmpresa = localStorage.getItem("empresa");
      if (savedEmpresa) {
        try {
          setEmpresa(JSON.parse(savedEmpresa));
        } catch (e) {
          console.error("Error parsing empresa:", e);
        }
      }
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      // Try superadmin endpoint first if no empresa saved
      const savedEmpresa = localStorage.getItem("empresa");
      if (!savedEmpresa) {
        try {
          const response = await axios.get(`${API_URL}/super/me`);
          setUser(response.data);
          setLoading(false);
          return;
        } catch (e) {
          // Not a superadmin, try regular auth
        }
      }
      
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error("Error fetching user:", error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  // Legacy login (for backwards compatibility)
  const login = async (email, senha) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, senha });
    const { token: newToken, user: userData } = response.data;
    
    localStorage.setItem("token", newToken);
    axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    
    return userData;
  };

  // Multi-tenant login
  const loginEmpresa = async (slug, email, senha) => {
    const response = await axios.post(`${API_URL}/empresas/${slug}/login`, { email, senha });
    const { token: newToken, user: userData, empresa: empresaData } = response.data;
    
    localStorage.setItem("token", newToken);
    localStorage.setItem("empresa", JSON.stringify(empresaData));
    axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    setEmpresa(empresaData);
    
    return { user: userData, empresa: empresaData };
  };

  // Super Admin login
  const loginSuperAdmin = async (email, senha) => {
    const response = await axios.post(`${API_URL}/super/login`, { email, senha });
    const { token: newToken, user: userData } = response.data;
    
    localStorage.setItem("token", newToken);
    localStorage.removeItem("empresa"); // Clear any empresa data
    axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    setEmpresa(null);
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("empresa");
    localStorage.removeItem("impersonateToken");
    localStorage.removeItem("impersonateUser");
    localStorage.removeItem("impersonateEmpresa");
    localStorage.removeItem("impersonateActive");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
    setEmpresa(null);
    setIsImpersonating(false);
  };

  // Exit impersonation mode and return to super admin dashboard
  const exitImpersonation = () => {
    localStorage.removeItem("impersonateToken");
    localStorage.removeItem("impersonateUser");
    localStorage.removeItem("impersonateEmpresa");
    localStorage.removeItem("impersonateActive");
    setIsImpersonating(false);
    // Close the current tab/window
    window.close();
  };

  // Refresh empresa data from server
  const refreshEmpresa = async (slug) => {
    try {
      const response = await axios.get(`${API_URL}/empresas/${slug}`);
      const updatedEmpresa = {
        ...empresa,
        id: response.data.id,
        nome: response.data.nome,
        slug: response.data.slug,
        logo_url: response.data.logo_url,
        banner_url: response.data.banner_url,
        cnpj_ein: response.data.cnpj_ein,
        sla_dias: response.data.sla_dias
      };
      localStorage.setItem("empresa", JSON.stringify(updatedEmpresa));
      setEmpresa(updatedEmpresa);
      return updatedEmpresa;
    } catch (error) {
      console.error("Error refreshing empresa:", error);
    }
    return empresa;
  };

  const register = async (nome, email, senha, role = "leitor") => {
    const response = await axios.post(`${API_URL}/auth/register`, {
      nome,
      email,
      senha,
      role
    });
    return response.data;
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      empresa, 
      token, 
      loading, 
      login, 
      loginEmpresa,
      loginSuperAdmin, 
      logout, 
      register,
      refreshEmpresa,
      isImpersonating,
      exitImpersonation
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
